import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, PlusSquare, List, IndianRupee, LogOut, ClipboardList, Menu, X } from 'lucide-react';
import { useAuth } from '../lib/auth';
import BrandMark from './BrandMark';
import '../styles/procurement-skin.css';

/**
 * Porta Cabin realm shell — sidebar + content outlet. Mirrors the procurement
 * layout pattern but scoped to the porta.crystalgrp.xyz realm.
 *
 * Realm accent is amber/orange (see AuthShell's REALM_THEME.porta), so the
 * brand mark, the active nav row and every primary action read `orange-600`.
 * Structure stays slate.
 *
 * On phones (<lg) the sidebar collapses into a drawer behind a hamburger —
 * a fixed 224px rail would eat two-thirds of a 360px screen.
 */
const NAV = [
  { to: '/porta', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/porta/orders', label: 'Order Dashboard', icon: ClipboardList },
  { to: '/porta/new', label: 'New Cabin', icon: PlusSquare },
  { to: '/porta/list', label: 'All Cabins', icon: List },
  { to: '/porta/cost-sheet', label: 'Cost Sheet', icon: IndianRupee },
];

export default function PortaLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile drawer toggle */}
      <button
        type="button"
        className="fixed left-3 top-3 z-40 flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white shadow-lg lg:hidden"
        onClick={() => setOpen((v) => !v)}
        aria-label="Toggle navigation"
      >
        {open ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Drawer backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-20 bg-slate-900/50 lg:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        // `fixed`, not `sticky`: html/body carry overflow-x:hidden, which makes an
        // ancestor a scroll container and silently breaks position:sticky.
        className={`fixed left-0 top-0 z-30 flex h-screen w-56 max-w-[80vw] shrink-0 flex-col bg-slate-900 text-slate-200 transition-transform lg:sticky lg:max-w-none lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="shrink-0 border-b border-slate-800 px-4 py-4">
          <div className="flex items-center gap-2">
            <BrandMark size={34} />
            <div className="min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-[2px] text-amber-500">Crystal Group</div>
              <div className="font-display font-extrabold leading-tight">Porta Cabin</div>
            </div>
          </div>
          <div className="mt-2 truncate text-[10px] text-slate-500" title={user?.email}>{user?.email} · {user?.portaRole?.toUpperCase()}</div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              className={({ isActive }) => `press flex items-center gap-2 rounded-sm px-3 py-2 font-display text-sm font-semibold transition-all duration-fast ease-out ${isActive ? 'bg-navy-400/[0.17] text-navy-300' : 'text-slate-300 hover:bg-slate-800'}`}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => { logout(); navigate('/porta/login'); }}
          className="press m-2 flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-800">
          <LogOut size={16} /> Sign out
        </button>
      </aside>
      <main className="procurement-skin min-w-0 max-w-full flex-1 [overflow-x:clip] p-3 pt-14 sm:p-4 sm:pt-14 lg:p-6 lg:pt-6">
        <Outlet />
      </main>
    </div>
  );
}
